import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { EngagementItem } from "PFApp/components/engagement_item";
import { SelectProps } from "PFComponents/select";
import { fetchActivitiesOptions } from "PFCore/services/activities";
import { ActivityOption, Id, Template } from "PFTypes";
import { useCallback } from "react";

import { BookingFormValues } from "../booking_form_values.types";
import { getOwnershipFilters, parseEngagementToOption } from "./engagement_selector.utils";

type UseEngagementOptionsFetcherParams = {
  workforceMember: BookingFormValues["workforceMember"];
  currentProfileId: Id;
  canManagePublicActivityBookings: boolean;
  engagementTemplateKey: Template["key"] | undefined;
  minTermLength: number;
};

export const useEngagementOptionsFetcher = ({
  workforceMember,
  currentProfileId,
  canManagePublicActivityBookings,
  engagementTemplateKey,
  minTermLength
}: UseEngagementOptionsFetcherParams) => {
  const { ownerId, contributorId } = getOwnershipFilters({
    workforceMember,
    currentProfileId,
    canManagePublicActivityBookings
  });

  const fetchOptions = useCallback<NonNullable<SelectProps<ActivityOption>["fetchOptions"]>>(
    async ({ ids, searchTerm }) => {
      if (isEmpty(ids) && (!searchTerm || searchTerm.length < minTermLength)) {
        return { entries: [], meta: { total: 0 } };
      }
      const response = await fetchActivitiesOptions({
        id: ids,
        term: searchTerm ?? "",
        templateKey: engagementTemplateKey,
        state: ["draft", "new"],
        ...(!isNil(ownerId) ? { owned: true } : {}),
        ...(!isNil(contributorId) ? { contributor: true } : {})
      });
      const options = response.entries.map((entry) => ({
        id: entry.id,
        value: entry.name,
        displayElement: <EngagementItem item={parseEngagementToOption(entry)} />,
        original: entry
      }));
      return {
        ...response,
        entries: options
      };
    },
    [engagementTemplateKey, ownerId, contributorId, minTermLength]
  );

  return { fetchOptions, ownerId, contributorId };
};
